import type { ToolApprovalCardProps, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { CardAvatar, DraftSurface, EditErrors, textEditor, useDraftMode } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { DraftTextArea } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { goodVibeSurfaceProps } from "#features/conversations/components/prepared-action/goodVibeSurface.ts";
import { isObject, readBoolean, readString, readStringList, toHttpsUrl, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { H3, Text } from "@hopper-ui/components";
import { IconLock, IconPeople } from "@intentui/icons";
import { useCallback, type Key } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import "./GoodVibeCard.css";

interface VisibilityEditor {
    isPublic: boolean;
    onChange: (isPublic: boolean) => void;
}

const MAX_AVATARS = 3;

// Officevibe's limit.
const GOOD_VIBE_MESSAGE_MAX_LENGTH = 1500;

const PUBLIC_KEY = "public";
const PRIVATE_KEY = "private";

interface Recipient {
    name: string;
    photoUrl: string | null;
}

// Core sends one entry per recipient id, in order, with a null name or photo it could not read. An entry without a
// name drops out.
function recipientsOf(fields: Fields): Recipient[] {
    const entries = Array.isArray(fields.recipients) ? fields.recipients : [];

    return entries.flatMap(entry => {
        if (!isObject(entry)) {
            return [];
        }
        const name = readString(entry, "name");

        return name === null ? [] : [{ name, photoUrl: toHttpsUrl(readString(entry, "photo_url")) }];
    });
}

function RecipientNames({ children }: { children: string }) {
    return <Text size="sm" fontWeight="body-sm-medium" color="neutral" className="wai-gv-names">{children}</Text>;
}

function Recipients({ recipients, count }: { recipients: Recipient[]; count: number }) {
    const { tShared } = useSharedTranslation();
    const peopleLabel = tShared("preparedAction.people", { count });

    const [first] = recipients;
    if (first === undefined) {
        return <RecipientNames>{peopleLabel}</RecipientNames>;
    }

    if (count === 1) {
        return (
            <div className="wai-gv-recipients">
                <CardAvatar name={first.name} photoUrl={first.photoUrl} />
                <RecipientNames>{first.name}</RecipientNames>
            </div>
        );
    }

    return (
        <div className="wai-gv-recipients">
            <span className="wai-gv-avatars">
                {recipients.slice(0, MAX_AVATARS).map(({ name, photoUrl }) => <CardAvatar key={name} name={name} photoUrl={photoUrl} />)}
            </span>
            <span className="wai-gv-recipient-text">
                <RecipientNames>{recipients.map(({ name }) => name).join(", ")}</RecipientNames>
                <Text size="xs" className="wai-pa-muted">{peopleLabel}</Text>
            </span>
        </div>
    );
}

// The selected segment stays neutral, so the category tint is the only colour.
function VisibilityToggle({ visibility }: { visibility: VisibilityEditor }) {
    const { tShared } = useSharedTranslation();
    const { isPublic, onChange } = visibility;

    const handleSelectionChange = useCallback((keys: Set<Key>) => {
        const [key] = keys;
        if (key !== undefined) {
            onChange(key === PUBLIC_KEY);
        }
    }, [onChange]);

    return (
        <ToggleButtonGroup
            aria-label={tShared("preparedAction.visibility")}
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[isPublic ? PUBLIC_KEY : PRIVATE_KEY]}
            onSelectionChange={handleSelectionChange}
            className="wai-gv-toggle"
        >
            <ToggleButton id={PUBLIC_KEY} className="wai-gv-toggle-item">
                <IconPeople aria-hidden="true" />
                {tShared("preparedAction.public")}
            </ToggleButton>
            <ToggleButton id={PRIVATE_KEY} className="wai-gv-toggle-item">
                <IconLock aria-hidden="true" />
                {tShared("preparedAction.private")}
            </ToggleButton>
        </ToggleButtonGroup>
    );
}

function VisibilityLabel({ isPublic }: { isPublic: boolean }) {
    const { tShared } = useSharedTranslation();

    return (
        <Text size="xs" fontWeight="body-xs-medium" className="wai-gv-visibility">
            {isPublic ? <IconPeople aria-hidden="true" /> : <IconLock aria-hidden="true" />}
            {tShared(isPublic ? "preparedAction.public" : "preparedAction.private")}
        </Text>
    );
}

function GoodVibeCard(props: ToolApprovalCardProps) {
    const { args, display, edits, onEditArgs, editErrors } = props;
    const { tShared } = useSharedTranslation();
    const mode = useDraftMode();
    const fields = display ?? {};
    const recipients = recipientsOf(fields);
    // A name core could not read drops out, so the ids still give the recipient count.
    const recipientCount = Math.max(readStringList(args, "recipient_member_ids").length, recipients.length);
    const promptText = readString(fields, "prompt_text");
    const categoryText = readString(fields, "category_text");
    const isPublic = typeof edits.is_public === "boolean" ? edits.is_public : readBoolean(args, "is_public", true);
    const editor = textEditor(props, "message");
    const surface = goodVibeSurfaceProps(readString(fields, "category_id"));

    return (
        <DraftSurface className={surface.className} style={surface.style}>
            <div className="wai-gv-header">
                <div className="wai-gv-heading">
                    {promptText !== null && (
                        <H3 fontSize="body-sm" fontWeight="body-sm-semibold" lineHeight="body-sm" color="neutral" className="wai-pa-title">{promptText}</H3>
                    )}
                    {categoryText !== null
                        ? <Text size="xs" fontWeight="body-xs-medium" color="neutral" className="wai-gv-category">{categoryText}</Text>
                        : <Text size="xs" fontWeight="body-xs-medium" className="wai-pa-muted">{tShared("preparedAction.goodVibe")}</Text>}
                </div>
                {mode === "editing"
                    ? <VisibilityToggle visibility={{ isPublic, onChange: value => onEditArgs({ is_public: value }) }} />
                    : <VisibilityLabel isPublic={isPublic} />}
            </div>

            <Recipients recipients={recipients} count={recipientCount} />

            {mode === "settled"
                ? <p className="wai-gv-sent wai-heading-font">{editor.value}</p>
                : <DraftTextArea editor={editor} label={tShared("preparedAction.goodVibeMessage")} maxLength={GOOD_VIBE_MESSAGE_MAX_LENGTH} isDisabled={mode === "sending"} />}
            <EditErrors errors={editErrors} />
        </DraftSurface>
    );
}

export const sendGoodVibeRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "message") !== null,
    Card: GoodVibeCard,
    decisionLabels: { allow: "preparedAction.sendGoodVibe", deny: "preparedAction.discard" }
};
